import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { StoreProps } from '@/app/models/store'
import {
    LuxAccordion,
    LuxCheckboxInput,
    LuxIcon,
    LuxRadioInput,
    LuxRichInputText,
    LuxDialog,
    Color,
} from '@lux/wardrobe-lib'
import { ProjectApiCreateActivityBody } from '@/app/models/projects-service'
import ReassignStoreTasks, { ReassignStoreProps } from './ReassignSecondStep'
import { connect } from 'react-redux'
import { saveDelegation } from '@/app/redux/actions/activity-execution'
import { DelegateType, TypeReassignment } from '@/app/models/assignee'
import { NEW_ACTIVITY_EXECUTION_NAMESPACE } from '@/app/redux/reducers/activity-execution'
import { SmartlyActionSubtypes } from '@Techedge/digitalportal-shared'
import './ReassignDialog.scss'
import { SectionFilter } from '@/app/models/search'

export enum ReassignmentSize {
    ENTIRE = 'entire',
    PARTIAL = 'partial',
}
interface ReassignDialogProps {
    visible: boolean
    projectDetails: ProjectApiCreateActivityBody
    storesList: StoreProps[]
    delegateWhat: DelegateType
    storeId?: string
    isApproval: boolean
    hideReassignmentSize?: boolean
    disclaimer?: string
    onHide: () => void
    onSaveDelegation: (
        projectId: string,
        storeId: string,
        delegateBody: any,
        successCallback: () => void
    ) => void
}

const ReassignDialog = ({
    visible,
    projectDetails,
    storesList,
    delegateWhat,
    storeId,
    isApproval,
    hideReassignmentSize,
    disclaimer,
    onSaveDelegation,
    onHide,
}: ReassignDialogProps) => {
    let emptySections = {
        questions: [],
        jobs: [],
        surveys: [],
    }

    const [activeStep, setActiveStep] = useState(0)
    const [selectedStores, setSelectedStores] = useState<ReassignStoreProps[]>([])
    const [closedStoresCount, setClosedStoresCount] = useState<number>(0)
    const [reassignmentSize, setReassignmentSize] = useState<ReassignmentSize>(
        hideReassignmentSize ? ReassignmentSize.ENTIRE : null
    )
    const [selectedSections, setSelectedSections] = useState<{
        questions: string[]
        jobs: string[]
        surveys: string[]
    }>(emptySections)
    const [reassignReason, setReassignReason] = useState<any>()

    const intl = useIntl()

    const handleNext = () => {
        setActiveStep((prevStep) => prevStep + 1)
    }

    const handleBack = () => {
        setActiveStep((prevStep) => prevStep - 1)
    }

    const handleConfirm = () => {
        onSaveDelegation(
            projectDetails._id,
            storeId,
            {
                type: delegateWhat,
                storeInfoAssignees: selectedStores.map((store) => ({
                    storeId: store.id,
                    typeReassignment: store.reassignmentType,
                    delegateRole: store.reassignRole,
                    delegateUserId: store.assigneeName,
                })),
                jobIds:
                    reassignmentSize === ReassignmentSize.ENTIRE
                        ? projectDetails.jobs?.map((j) => j._id)
                        : selectedSections.jobs,
                surveyIds:
                    reassignmentSize === ReassignmentSize.ENTIRE
                        ? projectDetails.surveys?.map((s) => s._id)
                        : selectedSections.surveys,
                questionIds:
                    reassignmentSize === ReassignmentSize.ENTIRE
                        ? projectDetails.questions?.map((q) => q._id)
                        : selectedSections.questions,
                reason: reassignReason,
            },
            () => {
                setActiveStep(0)
                setSelectedSections(emptySections)
                setSelectedStores([])
                setReassignmentSize(undefined)
                setReassignReason('')
                onHide()
            }
        )
    }

    useEffect(() => {
        if (storesList) {
            setSelectedStores(
                storesList
                    .filter((s) => !isStoreDisabled(s))
                    .map((store) => ({
                        id: store.storeId,
                        name: store.storeName,
                        reassignmentType: TypeReassignment.substitutive,
                        delegateType: delegateWhat,
                    }))
            )
            setClosedStoresCount(storesList.filter(isStoreDisabled).length)
        }
    }, [storesList])

    const dots = [0, 1, 2].map((step) => (
        <div key={step} className={`reassign-dialog-dot${activeStep === step ? ' active' : ''}`} />
    ))

    const isStoreDisabled = (store) => {
        if (
            [
                SmartlyActionSubtypes.PICTURE_MGMT,
                // SmartlyActionSubtypes.SERVICE_REQUEST
            ].includes(projectDetails?.type as SmartlyActionSubtypes)
        ) {
            return (
                store.status == SectionFilter.closed ||
                (delegateWhat === DelegateType.approver &&
                    projectDetails?.status.toString() == SectionFilter.new) ||
                (delegateWhat === DelegateType.approver && !store.isEnabledApprove) ||
                (delegateWhat === DelegateType.executor && !store.isEnabledExec)
            )
        }
        // if (projectDetails?.type === SmartlyActionSubtypes.SERVICE_REQUEST)
        //     return (
        //         store.status == SectionFilter.closed ||
        //         (delegateWhat === DelegateType.approver && !store.canAcceptApproval) ||
        //         (delegateWhat === DelegateType.executor && !store.isEnabledExec)
        //     )

        return false
    }

    const steps = [
        {
            id: 0,
            description: intl.formatMessage({
                defaultMessage: 'To reassign tasks, confirm the stores and click continue.',
                id: 'ph2.modals.reassign_dialog.first_description',
            }),
            content: (
                <div>
                    {storesList?.map((store, i) => (
                        <LuxCheckboxInput
                            key={i}
                            label={`${store.storeName} ${
                                store.status == SectionFilter.closed
                                    ? `(${intl.formatMessage({
                                          defaultMessage: 'Closed',
                                          id: 'smart.project.status.closed',
                                      })})`
                                    : ''
                            }`}
                            value={!!selectedStores.find((s) => s.id === store.storeId)}
                            onChangeCallback={(v) => {
                                v
                                    ? setSelectedStores([
                                          ...selectedStores,
                                          {
                                              id: store.storeId,
                                              name: store.storeName,
                                              reassignmentType: TypeReassignment.substitutive,
                                              delegateType: delegateWhat,
                                          },
                                      ])
                                    : setSelectedStores(
                                          selectedStores.filter((s) => s.id !== store.storeId)
                                      )
                            }}
                            classes="my-1 ml-1"
                            disabled={isStoreDisabled(store)}
                        />
                    ))}
                </div>
            ),
            info:
                intl.formatMessage({
                    id: 'ph2.modals.reassign_dialog.closed_stores_warning',
                    defaultMessage: 'Tasks that are closed cannot be reassigned.',
                }) + `(${closedStoresCount})`,
            isCompleted: () => selectedStores.length > 0,
        },
        {
            id: 1,
            description: (
                <div>
                    <span>
                        {intl.formatMessage({
                            defaultMessage:
                                'To continue, select the type of reassignment and complete the required information',
                            id: 'ph2.modals.reassign_dialog.second_description',
                        }) + '('}
                    </span>
                    <span className="mandatory">*</span>
                    <span>{').'}</span>
                </div>
            ),
            content: (
                <div>
                    {!hideReassignmentSize && (
                        <>
                            <div
                                className={
                                    'reassignment-form-box' +
                                    (reassignmentSize == ReassignmentSize.ENTIRE ? ' active' : '')
                                }>
                                <LuxRadioInput
                                    value={reassignmentSize}
                                    onChangeCallback={() => {
                                        setReassignmentSize(ReassignmentSize.ENTIRE)
                                        setSelectedSections({
                                            questions: [],
                                            jobs: [],
                                            surveys: [],
                                        })
                                    }}
                                    radioList={[
                                        {
                                            label: intl.formatMessage({
                                                id: 'ph2.modals.reassign_dialog.entire',
                                                defaultMessage: 'Reassign entire task',
                                            }),
                                            value: ReassignmentSize.ENTIRE,
                                        },
                                    ]}
                                />
                            </div>

                            <div
                                className={
                                    'reassignment-form-box' +
                                    (reassignmentSize == ReassignmentSize.PARTIAL ? ' active' : '')
                                }>
                                <LuxRadioInput
                                    value={reassignmentSize}
                                    onChangeCallback={() =>
                                        setReassignmentSize(ReassignmentSize.PARTIAL)
                                    }
                                    radioList={[
                                        {
                                            label: intl.formatMessage({
                                                id: 'ph2.modals.reassign_dialog.partial',
                                                defaultMessage: 'Select the section to reassign',
                                            }),
                                            value: ReassignmentSize.PARTIAL,
                                        },
                                    ]}
                                />
                                <div>
                                    {projectDetails &&
                                        Object.keys(selectedSections).map(
                                            (section, i) =>
                                                projectDetails[section]?.length > 0 && (
                                                    <div className="ml-5" key={i}>
                                                        <LuxCheckboxInput
                                                            value={
                                                                selectedSections[section].length ===
                                                                projectDetails[section].length
                                                            }
                                                            classes="my-2"
                                                            label={intl.formatMessage({
                                                                id: `ph2.modals.delegate.label.${section}`,
                                                                defaultMessage: section,
                                                            })}
                                                            onChangeCallback={(val) => {
                                                                setReassignmentSize(
                                                                    ReassignmentSize.PARTIAL
                                                                )
                                                                val
                                                                    ? setSelectedSections({
                                                                          ...selectedSections,
                                                                          [section]: projectDetails[
                                                                              section
                                                                          ].map((q) => q._id),
                                                                      })
                                                                    : setSelectedSections({
                                                                          ...selectedSections,
                                                                          [section]: [],
                                                                      })
                                                            }}
                                                        />
                                                        <div className="ml-2">
                                                            {projectDetails[section].map(
                                                                (item, index) => (
                                                                    <LuxCheckboxInput
                                                                        key={index}
                                                                        label={
                                                                            item.title ||
                                                                            intl.formatMessage({
                                                                                id: `ph2.modals.delegate.label.${section}`,
                                                                                defaultMessage:
                                                                                    section,
                                                                            }) + ` ${index + 1}`
                                                                        }
                                                                        value={selectedSections[
                                                                            section
                                                                        ].includes(item._id)}
                                                                        onChangeCallback={(v) => {
                                                                            setReassignmentSize(
                                                                                ReassignmentSize.PARTIAL
                                                                            )
                                                                            v
                                                                                ? setSelectedSections(
                                                                                      {
                                                                                          ...selectedSections,
                                                                                          [section]:
                                                                                              [
                                                                                                  ...selectedSections[
                                                                                                      section
                                                                                                  ],
                                                                                                  item._id,
                                                                                              ],
                                                                                      }
                                                                                  )
                                                                                : setSelectedSections(
                                                                                      {
                                                                                          ...selectedSections,
                                                                                          [section]:
                                                                                              selectedSections[
                                                                                                  section
                                                                                              ].filter(
                                                                                                  (
                                                                                                      s
                                                                                                  ) =>
                                                                                                      s !==
                                                                                                      item._id
                                                                                              ),
                                                                                      }
                                                                                  )
                                                                        }}
                                                                        classes="my-2"
                                                                    />
                                                                )
                                                            )}
                                                        </div>
                                                    </div>
                                                )
                                        )}
                                </div>
                            </div>
                        </>
                    )}
                    <div className="bold mb-3">
                        {intl.formatMessage({
                            id: 'ph2.modals.reassign_dialog.tasks',
                            defaultMessage: 'Tasks',
                        })}
                    </div>
                    {selectedStores.length > 1 ? (
                        <div className="reassign-store-accordion">
                            {selectedStores.map((store) => (
                                <LuxAccordion
                                    extraIcon={store.reassignRole ? 'status-ok' : undefined}
                                    extraIconColor={Color.GREEN}
                                    title={store.name}
                                    key={store.id}>
                                    <ReassignStoreTasks
                                        storeProps={store}
                                        setStore={(val) => {
                                            let tmpSelectedStores = [...selectedStores]
                                            let foundIdx = tmpSelectedStores.findIndex(
                                                (s) => store.id === s.id
                                            )
                                            if (foundIdx !== -1) tmpSelectedStores[foundIdx] = val
                                            setSelectedStores(tmpSelectedStores)
                                        }}
                                        projectDetails={projectDetails}
                                    />
                                </LuxAccordion>
                            ))}
                        </div>
                    ) : (
                        <ReassignStoreTasks
                            storeProps={selectedStores[0]}
                            setStore={(val) => setSelectedStores([val])}
                            projectDetails={projectDetails}
                        />
                    )}
                </div>
            ),
            info: intl.formatMessage({
                id: 'ph2.modals.reassign_dialog.cumulative_reassignment_warning',
                defaultMessage:
                    '[Cumulative reassignment means that the task can be executed from both the original and the new assignee.]',
            }),
            isCompleted: () => {
                return (
                    !selectedStores.find((s) => !s.reassignRole) &&
                    (reassignmentSize === ReassignmentSize.ENTIRE ||
                        (reassignmentSize === ReassignmentSize.PARTIAL &&
                            (selectedSections.questions.length > 0 ||
                                selectedSections.jobs.length > 0 ||
                                selectedSections.surveys.length > 0)))
                )
            },
        },
        {
            id: 0,
            description: intl.formatMessage({
                defaultMessage:
                    'To conclude, enter a reason for the reassignment and click confirm.',
                id: 'ph2.modals.reassign_dialog.third_description',
            }),
            content: (
                <div>
                    <LuxRichInputText
                        value={reassignReason}
                        onChangeCallback={(val) => setReassignReason(val)}
                    />
                </div>
            ),
            info:
                intl.formatMessage({
                    id: 'ph2.modals.reassign_dialog.assignment_role_warning',
                    defaultMessage: 'This task is now to be assigned to a SM and a SA.',
                }) + `(${closedStoresCount})`,
        },
    ]

    const actions = []

    if (activeStep > 0)
        actions.push({
            text: intl.formatMessage({
                id: 'ph2.modals.reassign_dialog.back',
                defaultMessage: 'Back',
            }),
            onClick: handleBack,
            variant: 'outlined',
        })

    if (activeStep < 2)
        actions.push({
            text: intl.formatMessage({
                id: 'ph2.modals.reassign_dialog.continue',
                defaultMessage: 'Continue',
            }),
            disabled: !steps[activeStep].isCompleted(),
            onClick: handleNext,
            color: Color.WARNING,
        })
    else
        actions.push({
            text: intl.formatMessage({
                id: 'ph2.modals.reassign_dialog.confirm',
                defaultMessage: 'Confirm',
            }),
            onClick: handleConfirm,
            color: Color.WARNING,
        })

    return (
        <LuxDialog
            size="lg"
            visible={visible}
            title={intl.formatMessage({
                id: 'ph2.modals.reassign_dialog.title',
                defaultMessage: 'Reassign',
            })}
            onHide={() => {
                onHide()
                setActiveStep(0)
            }}
            actions={actions}
            classes="reassign-dialog"
            customActions>
            <div className="reassign-modal-body">
                <div className="description bold">{steps[activeStep].description}</div>
                {disclaimer && (
                    <div className="flex align-items-center mt-4 mb-5">
                        <LuxIcon icon="info" size={16} classes="mr-2" />
                        <span className="text-mini">{disclaimer}</span>
                    </div>
                )}
                <div className="bold mb-3 mt-5">{projectDetails?.projectTitle}</div>
                <div className="content">{steps[activeStep].content}</div>
                <div className="flex align-items-center mt-4 mb-5">
                    <LuxIcon icon="info" size={16} classes="mr-2" />
                    <span className="text-mini">{steps[activeStep].info}</span>
                </div>
            </div>

            <div className="flex">{dots}</div>
        </LuxDialog>
    )
}

const mapStateToProps = (state: any) => ({
    isApproval: state[NEW_ACTIVITY_EXECUTION_NAMESPACE].isApproval,
})

const mapDispatchToProps = (dispatch: any) => ({
    onSaveDelegation: (projectId, storeId, delegateBody, successCallback) =>
        dispatch(saveDelegation(projectId, storeId, delegateBody, successCallback)),
})

export default connect(mapStateToProps, mapDispatchToProps)(ReassignDialog)
